import NiceModal from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { ADMIN, AUTOCOMPLETE, ENUMS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

const ActionRoleModal = NiceModal.create(({ modalName }) => {
	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					actionType: yup.string().nullable().required(),
					actionPage: yup.string().nullable().required(),
					actionRole: yup.string().nullable().required(),
					roleIDs: yup.array().nullable(),
					moduleIDs: yup.array().nullable(),
					disableModuleIDs: yup.array().nullable(),
					packageRoleIds: yup.array().nullable(),
					countryIDs: yup.array().nullable(),
					clients: yup.array().nullable()
				})
				.required(),
		[]
	);

	const dispatch = useDispatch();
	const { showError, showSuccess } = useAlert();
	const axiosPrivate = useAxiosPrivate();
	const { closeModal } = useModalRouter();

	const handleCloseModal = () => {
		closeModal();
	};

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			actionType: null,
			actionPage: null,
			actionRole: null,
			roleIDs: null,
			moduleIDs: null,
			disableModuleIDs: null,
			packageRoleIds: null,
			countryIDs: null,
			clients: null
		}
	});

	const {
		setValue,
		watch,
		handleSubmit,
		formState: { isDirty, isSubmitSuccessful, isSubmitting }
	} = formMethods;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);

	const onSubmit = (data) => {
		axiosPrivate
			.post(ADMIN.ROLE.ADD_ROLES, {
				path: `${data.actionType.trim() + data.actionPage.trim()}/${data.actionRole.trim()}`,
				roleIDs: data.roleIDs?.length > 0 ? data.roleIDs?.join(',') : null,
				moduleIDs: data.moduleIDs?.length > 0 ? data.moduleIDs?.join(',') : null,
				disableModuleIDs: data.disableModuleIDs?.length > 0 ? data.disableModuleIDs?.join(',') : null,
				packageRoleIds: data.packageRoleIds?.length > 0 ? data.packageRoleIds?.join(',') : null,
				countryIDs: data.countryIDs?.length > 0 ? data.countryIDs?.join(',') : null,
				clients: data.clients?.length > 0 ? data.clients?.join(',') : null
			})
			.then(() => {
				showSuccess();
				dispatch(updateDynamicListData(DYNAMICLISTS.ACCESS_CONTROL));
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			});
		// .finally(handleCloseModal);
	};

	const defaultColSpan = {
		md: 6,
		lg: 4
	};

	const roleAutocompleteColumns = useMemo(
		() => [
			{ accessor: 'ID', width: 100 },
			{ accessor: 'name', width: 1000 }
		],
		[]
	);

	const countryAutocompleteColumns = useMemo(
		() => [
			{ accessor: 'ID', width: 100 },
			{ accessor: 'nameEn', width: 1000 }
		],
		[]
	);

	const clientsAutocompleteColumns = useMemo(
		() => [
			{ accessor: 'ID', width: 100 },
			{ accessor: 'Name', width: 1000 }
		],
		[]
	);

	return (
		<Modal maxWidth="large" name={modalName} fullwidth className="modal-actionRoleModal">
			<ModalTitle>
				<Typography variant="p1">addActionRole</Typography>
			</ModalTitle>

			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem colSpan={defaultColSpan} label="actionType" required>
						<EnumSelect
							name="actionType"
							data={['/rowActions', '/actions']}
							value={watch('actionType')}
							setValue={(value) => {
								setValue('actionType', value, { shouldValidate: true, shouldDirty: true });
							}}
							searchable={false}
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="actionPage" required>
						<EnumSelect
							name="actionPage"
							enumName={ENUMS.OTO_V2_ACTION_PAGES}
							value={watch('actionPage')}
							setValue={(value) => {
								setValue('actionPage', value, { shouldValidate: true, shouldDirty: true });
							}}
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="actionRole" required>
						<EnumSelect
							name="actionRole"
							enumName={ENUMS.OTO_V2_ACTION_ROLES}
							value={watch('actionRole')}
							setValue={(value) => {
								setValue('actionRole', value, { shouldValidate: true, shouldDirty: true });
							}}
						/>
					</FormItem>

					<FormItem label="Role IDs">
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.ROLES}
							name="roleIDs"
							value={watch('roleIDs')}
							setValue={(value) => {
								setValue('roleIDs', value?.map((item) => item.ID) ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							columns={roleAutocompleteColumns}
							selectionLabelAccessor={['name']}
							multipleSelection
						/>
					</FormItem>

					<FormItem label="Module IDs">
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.ROLES}
							name="moduleIDs"
							value={watch('moduleIDs')}
							setValue={(value) => {
								setValue('moduleIDs', value?.map((item) => item.ID) ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							columns={roleAutocompleteColumns}
							selectionLabelAccessor={['name']}
							multipleSelection
						/>
					</FormItem>

					<FormItem label="Disable if module exists">
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.ROLES}
							name="disableModuleIDs"
							value={watch('disableModuleIDs')}
							setValue={(value) => {
								setValue('disableModuleIDs', value?.map((item) => item.ID) ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							columns={roleAutocompleteColumns}
							selectionLabelAccessor={['name']}
							multipleSelection
						/>
					</FormItem>

					<FormItem label="Packages">
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.ROLES}
							value={watch('packageRoleIds')}
							setValue={(value) => {
								setValue('packageRoleIds', value?.map((item) => item.ID) ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							name="packageRoleIds"
							columns={roleAutocompleteColumns}
							selectionLabelAccessor={['name']}
							multipleSelection
						/>
					</FormItem>

					<FormItem label="Country">
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.COUNTRIES}
							value={watch('countryIDs')}
							setValue={(value) => {
								setValue('countryIDs', value?.map((item) => item.ID) ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							name="countryIDs"
							columns={countryAutocompleteColumns}
							selectionLabelAccessor={['nameEn']}
							multipleSelection
						/>
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="clients">
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.CLIENTS}
							value={watch('clients')}
							setValue={(value) => {
								setValue('clients', value?.map((item) => item.ID) ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							name="clients"
							columns={clientsAutocompleteColumns}
							selectionLabelAccessor={['name']}
							multipleSelection
						/>
					</FormItem>
				</Form>
			</ModalContent>

			<ModalActions>
				<Button variant="outline" label="cancel" onClick={handleCloseModal} />
				<Button
					label="add"
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
				/>
			</ModalActions>
		</Modal>
	);
});

export default ActionRoleModal;
